// Identity/verification state via vue-query. The cookie is HttpOnly; the
// source of truth for "am I verified" is GET /api/identity/me.
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { getJson, postJson, ApiError } from '@/shared/api/client'
import type { IdentityMe, VerifyResponse } from '@/shared/api/types'

export function useIdentity() {
    const queryClient = useQueryClient()

    const me = useQuery<IdentityMe | null>({
        queryKey: ['identity', 'me'],
        queryFn: async () => {
            try {
                return await getJson<IdentityMe>('/api/identity/me')
            } catch (err) {
                if (err instanceof ApiError && err.status === 401) return null
                throw err
            }
        },
        staleTime: 30_000,
        retry: false,
    })

    const start = useMutation({
        mutationFn: (email: string) => postJson<{ sent: boolean }>('/api/identity/start', { email }),
    })

    const verify = useMutation({
        mutationFn: (input: { email: string; code: string }) =>
            postJson<VerifyResponse>('/api/identity/verify', input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['identity', 'me'] })
        },
    })

    return { me, start, verify }
}
